import { useQueryClient, useQuery, skipToken } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { IApplicationData, IBudgetConstruction } from '@apply/api/src/api/ApplyApi';
import { applicationQueryKey } from './applicationQueries';

/**
 * Reads and writes a single field on the Construction Budget form in the React Query cache.
 * Finds the form by type === 'Budget' and propertyGuid.
 *
 * Replaces `const [x, set_x] = useRecoilState(someAtom_state(propertyGuid))` for budget atoms.
 *
 * @param propertyGuid - GUID of the property the budget belongs to
 * @param selector     - reads the field value from IBudgetConstruction
 * @param updater      - returns a new IBudgetConstruction with the field updated
 * @returns            [currentValue, setter]
 */
export function useBudgetConstructionField<T>(
    propertyGuid: string,
    selector: (budget: IBudgetConstruction) => T,
    updater: (budget: IBudgetConstruction, value: T) => IBudgetConstruction
): [T | undefined, (value: T) => void] {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const queryClient = useQueryClient();

    const { data: currentValue } = useQuery<IApplicationData, Error, T | undefined>({
        queryKey: applicationQueryKey(entryGuid ?? ''),
        queryFn: skipToken,
        select: (d) => {
            const form = d.information?.properties?.forms?.find(
                (f) => f.type === 'Budget' && f.propertyGuid === propertyGuid
            ) as IBudgetConstruction | undefined;
            return form !== undefined ? selector(form) : undefined;
        },
    });

    const setValue = (value: T) => {
        if (entryGuid === undefined) return;
        queryClient.setQueryData<IApplicationData>(applicationQueryKey(entryGuid), (old) => {
            if (!old) return old;
            const forms = old.information?.properties?.forms ?? [];
            const idx = forms.findIndex(
                (f) => f.type === 'Budget' && f.propertyGuid === propertyGuid
            );
            if (idx === -1) return old;
            const form = forms[idx] as IBudgetConstruction;
            const newForms = [...forms];
            newForms[idx] = updater(form, value);
            return {
                ...old,
                information: {
                    ...old.information,
                    properties: {
                        ...old.information.properties,
                        forms: newForms,
                    },
                },
            };
        });
    };

    return [currentValue, setValue];
}
